import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import moment from 'moment';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { DocumentService } from '@app/shared/services';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@AutoUnsubscribe()
@Component({
  selector: 'change-pin-dialog',
  templateUrl: './change-pin-dialog.component.html',
  styleUrls: ['./change-pin-dialog.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePinDialogComponent implements OnInit, OnDestroy {
  stateObject;
  screenWidth;
  vaultStateObject$: any;
  changePinForm: UntypedFormGroup;
  financialAttributeForm: UntypedFormGroup;
  subscription: Subscription = new Subscription();
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ChangePinDialogComponent>,
    private toastr: ToastrService,
    private _fb: UntypedFormBuilder,
    private documentService: DocumentService,
    private vaultService: VaultStoreEffects,
    private changeDetectionRef: ChangeDetectorRef
  ) {
    this.screenWidth = window.innerWidth;
    this.vaultStateObject$ = this.vaultService.vaultObject;
    this.subscription.add(
      this.vaultStateObject$.subscribe((item) => {
        this.stateObject = item;
        this.changeDetectionRef.markForCheck();
      })
    );
  }

  ngOnInit(): void {
    this.changePinForm = this._fb.group({
      pin: [this.data.pin, Validators.compose([Validators.required, Validators.minLength(4), Validators.maxLength(6)])],
    });
    this.financialAttributeForm = this._fb.group({
      value: ['', [Validators.required]],
      effective_date: ['', [Validators.required]],
    });
  }

  ngAfterViewInit() {
    if (this.data.financialData.value) this.financialAttributeForm.controls['value'].setValue(this.data.financialData.value);
    if (this.data.financialData.effective_date)
      this.financialAttributeForm.controls['effective_date'].setValue(this.data.financialData.effective_date);
  }

  updateVaultUserFormLink() {
    if (!this.changePinForm.valid) {
      this.changePinForm.controls['pin'].markAsTouched();
      this.changeDetectionRef.detectChanges();
      return;
    }
    const payload = {
      vault_user_form_id: this.data?.vaultFormId,
      user_id: this.stateObject.userId,
      pin: this.changePinForm.controls['pin'].value,
      use_pin: true,
    };
    this.subscription.add(
      this.documentService.updateVaultUserFormLink(payload).subscribe(
        (data) => {
          this.toastr.success('PIN updated');
          this.dialogRef.close({ data: data });
          this.changeDetectionRef.detectChanges();
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }

  createVaultUserFormPurchasePrice() {
    if (!this.financialAttributeForm.valid || this.financialAttributeForm.controls['value'].value == 0) return;
    let formatedDate = moment(this.financialAttributeForm.controls['effective_date'].value).format('YYYY-MM-DD');
    let formatedValue = { value: Number(this.financialAttributeForm.controls['value'].value) };
    const payload = {
      vault_user_form_id: this.data?.vaultFormId,
      value: JSON.stringify(formatedValue),
      type: this.data?.type,
      effective_date: formatedDate,
    };
    this.subscription.add(
      this.documentService.createVaultUserFormFinancialAttribute(payload).subscribe(
        (data) => {
          this.toastr.success(`${this.data?.title} added successfully`);
          this.dialogRef.close({ data: data });
          this.changeDetectionRef.detectChanges();
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }

  updateVaultUserFormPurchasePrice() {
    if (!this.financialAttributeForm.valid || this.financialAttributeForm.controls['value'].value == 0) return;
    let formatedDate = moment(this.financialAttributeForm.controls['effective_date'].value).format('YYYY-MM-DD');
    let formatedValue = { value: Number(this.financialAttributeForm.controls['value'].value) };
    const payload = {
      id: this.data?.financialData?.id,
      vault_user_form_id: this.data?.vaultFormId,
      value: JSON.stringify(formatedValue),
      effective_date: formatedDate,
    };
    this.subscription.add(
      this.documentService.updateVaultUserFormFinancialAttribute(payload).subscribe(
        (data) => {
          this.toastr.success(`${this.data?.title} updated successfully`);
          this.dialogRef.close({ data: data });
          this.changeDetectionRef.detectChanges();
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }

  getValue(event) {
    const strFix = `${event}`.replace(/,/g, '');
    this.financialAttributeForm.controls['value'].patchValue(strFix);
  }

  selectAllContent(event) {
    event.target.select();
  }

  handleInput(event) {
    this.changePinForm.controls['pin'].setValue(event.target.value);
    this.changeDetectionRef.detectChanges();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
